import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import { capitalize } from 'lodash';
import React, { useCallback } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { IFGAGroupMapping } from 'types/api';
import { IOption } from 'types/fields';
import { ID, useMappingEditor } from 'hooks/useMappingEditor';
import FGAMappingItem from './FGAMappingItem';

interface IProps {
	fgaType: string;
	relationOptions: IOption[];
	resourceOptions: IOption[];
	initialSectionMappings: IFGAGroupMapping[] | null;
	onSubmitSection: (
		fgaType: string,
		sectionMappings: IFGAGroupMapping[]
	) => void;
}

const FGAMappingSection: React.FC<IProps> = ({
	fgaType,
	relationOptions,
	resourceOptions,
	initialSectionMappings,
	onSubmitSection
}) => {
	const setExternalItems = useCallback(
		(items: IFGAGroupMapping[]) => onSubmitSection(fgaType, items),
		[fgaType, onSubmitSection]
	);

	const createEmptyItem = useCallback(
		(): IFGAGroupMapping => ({
			id: '',
			group: '',
			fgaType,
			relation: '',
			resource: resourceOptions.length === 1 ? resourceOptions[0].value : ''
		}),
		[fgaType, resourceOptions]
	);

	// Add stays off until the editor holds the stored rows: an add before then submits a lone empty
	// row as the whole section, dropping every mapping already saved.
	const { rows, addItem, editItems, deleteItem, persist, isReady } =
		useMappingEditor<IFGAGroupMapping>({
			initialItems: initialSectionMappings,
			setExternalItems,
			createEmptyItem
		});

	return (
		<Stack flexDirection="column" gap={2} mt={3}>
			<Typography variant="body2">{`${capitalize(fgaType)} ${intl.tr('ssoConfig.fields.fgaMapping.section.title')}`}</Typography>
			<TransitionGroup>
				{rows.map((row, i) => (
					<Collapse key={row[ID]}>
						<FGAMappingItem
							fgaType={fgaType}
							index={i}
							row={row}
							relationOptions={relationOptions}
							resourceOptions={resourceOptions}
							onEdit={(patch, persistNow) => editItems(i, patch, persistNow)}
							onDelete={() => deleteItem(i)}
							onPersist={persist}
						/>
					</Collapse>
				))}
			</TransitionGroup>
			<Box>
				<Button
					color="primary"
					startIcon={<AddIcon />}
					onClick={addItem}
					disabled={!isReady}
					data-testid={`fga-${fgaType}-mappings-add-button`}
				>
					<Typography variant="body1">
						{intl.tr('ssoConfig.fields.fgaMapping.addMapping')}
					</Typography>
				</Button>
			</Box>
		</Stack>
	);
};

export default FGAMappingSection;
